import React, { useState, useEffect } from 'react';

import {
  doc,
  collection,
  onSnapshot,
  setDoc,
  DocumentReference,
  query,
  where,
  orderBy as fbOrderBy,
  QueryConstraint,
} from 'firebase/firestore';
import { useFirebase } from '../firebase';

/**
 * Hook to listen to a single document in Firestore
 */
export const useDoc = <T>(path: string) => {
  const { db, userCredentials, error: authError } = useFirebase();
  const [snapshot, setSnapshot] = useState<T | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [ref, setRef] = useState<DocumentReference>();
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Reset error when dependencies change
    setError(null);

    // If path is empty, just return empty data
    if (!path) {
      setSnapshot(undefined);
      setIsLoading(false);
      return;
    }

    // If Firebase auth failed, don't try to access Firestore
    if (authError) {
      console.warn('Skipping Firestore access due to Firebase auth error:', authError);
      setSnapshot(undefined);
      setIsLoading(false);
      return;
    }

    if (!userCredentials) {
      // if the userCredentials are not set then our firebase connection is refreshing
      // Set a timeout to avoid infinite loading state if auth never completes
      const timeoutId = setTimeout(() => {
        console.warn('Timed out waiting for Firebase authentication in useDoc');
        setIsLoading(false);
        setError(new Error('Timed out waiting for Firebase authentication'));
      }, 5000); // 5 seconds timeout

      return () => clearTimeout(timeoutId);
    }

    let unsubscribe: (() => void) | undefined;

    try {
      const reference = doc(db, path);
      setRef(reference);

      unsubscribe = onSnapshot(
        reference,
        // Success handler
        snap => {
          const data = snap.data();
          setSnapshot(data as T);
          setIsLoading(false);
        },
        // Error handler
        err => {
          console.error(`Firestore error for document ${path}:`, err);
          setError(err);
          // Still set data to undefined and mark as loaded to avoid infinite loading state
          setSnapshot(undefined);
          setIsLoading(false);
        }
      );
    } catch (err) {
      console.error(`Error setting up Firestore listener for document ${path}:`, err);
      setError(err instanceof Error ? err : new Error('Firestore subscription error'));
      setSnapshot(undefined);
      setIsLoading(false);
    }

    // Cleanup function
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [path, userCredentials, db, authError]);

  return { snapshot, isLoading, ref, error };
};

/**
 * Function to set data on a document reference
 */
export const setData = <T>(
  ref: DocumentReference,
  data: Partial<T>,
  merge: boolean
): Promise<void> => {
  return setDoc(ref, data, { merge });
};

/**
 * Hook to listen to a collection in Firestore
 */
export const useCollection = <T>(path: string) => {
  const { db, userCredentials, error: authError } = useFirebase();
  const [snapshot, setSnapshot] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Reset error when dependencies change
    setError(null);

    // If path is empty, just return empty data
    if (!path) {
      setSnapshot([]);
      setIsLoading(false);
      return;
    }

    // If Firebase auth failed, don't try to access Firestore
    if (authError) {
      console.warn('Skipping Firestore access due to Firebase auth error:', authError);
      setSnapshot([]);
      setIsLoading(false);
      return;
    }

    if (!userCredentials) {
      // if the userCredentials are not set then our firebase connection is refreshing
      // Set a timeout to avoid infinite loading state if auth never completes
      const timeoutId = setTimeout(() => {
        console.warn('Timed out waiting for Firebase authentication in useCollection');
        setIsLoading(false);
        setError(new Error('Timed out waiting for Firebase authentication'));
      }, 5000); // 5 seconds timeout

      return () => clearTimeout(timeoutId);
    }

    let unsubscribe: (() => void) | undefined;

    try {
      const reference = collection(db, path);

      unsubscribe = onSnapshot(
        reference,
        // Success handler
        snap => {
          setSnapshot(
            snap.docs.map(d => ({
              ...d.data(),
              id: d.id,
            })) as T[]
          );
          setIsLoading(false);
        },
        // Error handler
        err => {
          console.error(`Firestore error for collection ${path}:`, err);
          setError(err);
          // Still set empty data and mark as loaded to avoid infinite loading state
          setSnapshot([]);
          setIsLoading(false);
        }
      );
    } catch (err) {
      console.error(`Error setting up Firestore listener for collection ${path}:`, err);
      setError(err instanceof Error ? err : new Error('Firestore subscription error'));
      setSnapshot([]);
      setIsLoading(false);
    }

    // Cleanup function
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [path, userCredentials, db, authError]);

  return { snapshot, isLoading, error };
};

/**
 * Hook to listen to a filtered collection in Firestore
 */
export const useFilteredCollection = <T>(
  path: string,
  conditions?: { field: string; operator: string; value: any }[],
  orderBy?: { field: string; direction: 'asc' | 'desc' }
) => {
  const { db, userCredentials, error: authError } = useFirebase();
  const [snapshot, setSnapshot] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Generate a unique ID for this hook instance to track in logs
  const hookId = useState(() => Math.random().toString(36).substr(2, 9))[0];

  // Create stable references for conditions and orderBy to prevent unnecessary re-renders
  // Use ref to avoid excessive re-renders while preserving the objects
  const valuesRef = React.useRef({
    lastPath: '',
    lastConditions: '',
    lastOrderBy: '',
    conditionsJSON: '',
    orderByJSON: null as string | null,
  });

  // Update the ref values if they've changed
  if (
    path !== valuesRef.current.lastPath ||
    JSON.stringify(conditions) !== valuesRef.current.lastConditions ||
    JSON.stringify(orderBy) !== valuesRef.current.lastOrderBy
  ) {
    valuesRef.current = {
      lastPath: path,
      lastConditions: JSON.stringify(conditions),
      lastOrderBy: JSON.stringify(orderBy),
      conditionsJSON: JSON.stringify(conditions),
      orderByJSON: orderBy ? JSON.stringify(orderBy) : null,
    };
  }

  // Use the stable references from the ref
  const conditionsRef = valuesRef.current.conditionsJSON;
  const orderByRef = valuesRef.current.orderByJSON;

  useEffect(() => {
    // Reset error when dependencies change
    setError(null);

    console.log(`[useFilteredCollection:${hookId}] Setting up subscription for path: ${path}`);

    // If path is empty, just return empty data
    if (!path) {
      setSnapshot([]);
      setIsLoading(false);
      return;
    }

    // If Firebase auth failed, don't try to access Firestore
    if (authError) {
      console.warn('Skipping Firestore access due to Firebase auth error:', authError);
      setSnapshot([]);
      setIsLoading(false);
      return;
    }

    if (!userCredentials) {
      // if the userCredentials are not set then our firebase connection is refreshing
      // Set a timeout to avoid infinite loading state if auth never completes
      const timeoutId = setTimeout(() => {
        console.warn('Timed out waiting for Firebase authentication in useFilteredCollection');
        setIsLoading(false);
        setError(new Error('Timed out waiting for Firebase authentication'));
      }, 5000); // 5 seconds timeout

      return () => clearTimeout(timeoutId);
    }

    let unsubscribe: (() => void) | undefined;

    try {
      const collectionRef = collection(db, path);

      // Build query with conditions and order
      const queryConstraints: QueryConstraint[] = [];

      // Parse the conditions back from the JSON string
      let parsedConditions = null;
      try {
        parsedConditions = conditionsRef ? JSON.parse(conditionsRef) : null;
      } catch (e) {
        console.error(`[useFilteredCollection:${hookId}] Failed to parse conditions:`, e);
      }

      // Add all conditions as constraints
      if (parsedConditions && parsedConditions.length > 0) {
        console.log(
          `[useFilteredCollection:${hookId}] Adding ${parsedConditions.length} condition constraints`
        );
        parsedConditions.forEach((c: any) => {
          if (c && c.field && c.operator) {
            queryConstraints.push(where(c.field, c.operator as any, c.value));
          }
        });
      }

      // Parse the orderBy back from the JSON string
      let parsedOrderBy = null;
      try {
        parsedOrderBy = orderByRef ? JSON.parse(orderByRef) : null;
      } catch (e) {
        console.error(`[useFilteredCollection:${hookId}] Failed to parse orderBy:`, e);
      }

      // Add orderBy as a constraint if provided and valid
      if (parsedOrderBy && parsedOrderBy.field && parsedOrderBy.direction) {
        console.log(`[useFilteredCollection:${hookId}] Adding orderBy constraint:`, parsedOrderBy);
        queryConstraints.push(fbOrderBy(parsedOrderBy.field, parsedOrderBy.direction));
      }

      // Create the query with all constraints
      const queryRef =
        queryConstraints.length > 0 ? query(collectionRef, ...queryConstraints) : collectionRef;

      console.log(`[useFilteredCollection:${hookId}] Creating snapshot listener for ${path}`, {
        hasConditions: !!parsedConditions?.length,
        hasOrderBy: !!parsedOrderBy,
      });

      unsubscribe = onSnapshot(
        queryRef,
        // Success handler
        snap => {
          console.log(
            `[useFilteredCollection:${hookId}] Received snapshot for ${path} with ${snap.docs.length} docs`
          );
          setSnapshot(
            snap.docs.map(d => ({
              ...d.data(),
              id: d.id,
            })) as T[]
          );
          setIsLoading(false);
        },
        // Error handler
        err => {
          console.error(`Firestore error for filtered collection ${path}:`, err);
          setError(err);
          // Still set empty data and mark as loaded to avoid infinite loading state
          setSnapshot([]);
          setIsLoading(false);
        }
      );
    } catch (err) {
      console.error(`Error setting up Firestore listener for filtered collection ${path}:`, err);
      setError(err instanceof Error ? err : new Error('Firestore subscription error'));
      setSnapshot([]);
      setIsLoading(false);
    }

    // Cleanup function
    return () => {
      console.log(`[useFilteredCollection:${hookId}] Cleaning up subscription for ${path}`);
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [path, conditionsRef, orderByRef, userCredentials, db, authError, hookId]);

  return { snapshot, isLoading, error };
};
